import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  Chip,
  Checkbox,
  Switch,
  Alert,
  Spinner,
  Icon,
  EmptyState,
  Pagination,
} from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import { useLocationScope } from '../../lib/LocationScopeContext';
import {
  vendorApiClient,
  type BulkAction,
  type BulkResult,
  type ProductFilter,
  type ProductListItem,
  type ProductsListResponse,
} from '../../lib/vendorApiClient';
import { formatCents, formatDay, todayDay } from '../../lib/money';
import {
  BORDER,
  GREEN_INK,
  HEADING,
  MUTED,
  ORANGE_INK,
  RED,
  FlagChip,
  PageTitle,
  ProductStatusChip,
  ProductThumb,
  departmentLabel,
  describeProductError,
} from './parts';
import BulkDialog, { type BulkDialogKind } from './BulkDialog';
import QuestionCards from './QuestionCards';

/**
 * P1 — Products list (Figma `fVTA2OnSEz4UOdTeoXeByH` 53:2).
 *
 * Search by name, brand, GLPC, barcode or the vendor's own SKU (§15b). Prices
 * and statuses are shown for ONE location at a time, because that is where
 * they live (§15n). Bulk actions apply to the selected products at that
 * location. A manufacturer's barcode is never shown — only the one we assigned.
 */

const FILTERS: ProductFilter[] = ['all', 'live', 'needs_answer', 'returned', 'verifying', 'with_us', 'stuck', 'hidden', 'on_sale', 'out_of_stock', 'recalled'];
const ATTENTION: ProductFilter[] = ['needs_answer', 'returned', 'stuck', 'recalled'];
const PAGE_SIZES = [10, 25, 50, 100];
const ALL_DEPARTMENTS = '__all';
const GRID = { xs: '32px 1fr 32px', md: '32px 2.3fr 1.5fr 1.4fr 1.5fr 0.7fr 28px' };

export default function ProductsList() {
  const { t, i18n } = useTranslation();
  const { activeVendorId, has } = useVendor();
  const { scope } = useLocationScope();
  const canEdit = has('catalog:edit');
  const canResolve = has('confirmation:resolve');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const status = (FILTERS.includes(params.get('status') as ProductFilter) ? params.get('status') : 'all') as ProductFilter;
  const department = params.get('department') ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const pageSize = PAGE_SIZES.includes(Number(params.get('pageSize'))) ? Number(params.get('pageSize')) : 25;
  const questionPage = Math.max(1, Number(params.get('qpage')) || 1);
  // The page's own picker wins; otherwise the top bar's location, when one is chosen.
  const storeId = params.get('storeId') ?? (scope !== 'all' ? scope : '');

  const [data, setData] = useState<ProductsListResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(q);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [dialog, setDialog] = useState<BulkDialogKind | null>(null);

  const patchParams = useCallback(
    (patch: Record<string, string | null>, keepPage = false) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          if (!keepPage) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Typing searches after a pause, not on every key.
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (search.trim() !== q) patchParams({ q: search.trim() || null });
    }, 300);
    return () => window.clearTimeout(id);
  }, [search, q, patchParams]);

  const requestSeq = useRef(0);
  const load = useCallback(() => {
    if (!activeVendorId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setLoadError(null);
    vendorApiClient
      .listProducts(activeVendorId, { q: q || undefined, storeId: storeId || undefined, status, department: department || undefined, page, pageSize })
      .then((r) => {
        // A slower, older answer must not replace a newer one.
        if (seq !== requestSeq.current) return;
        setData(r);
        setSelected(new Set());
      })
      .catch((e: Error) => seq === requestSeq.current && setLoadError(describeProductError(e, t)))
      .finally(() => seq === requestSeq.current && setLoading(false));
  }, [activeVendorId, q, storeId, status, department, page, pageSize, t]);

  useEffect(load, [load]);

  const items = useMemo(() => data?.items ?? [], [data]);
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.vendorProductId));
  const location = data?.location ?? null;

  const runBulk = async (ids: string[], action: BulkAction, sale?: { saleStartsOn: string | null; saleEndsOn: string | null }) => {
    if (!activeVendorId || !location) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await vendorApiClient.bulkUpdateProducts(activeVendorId, { storeId: location.storeId, vendorProductIds: ids, action, ...sale });
      if (ids.length > 1 || r.skipped.length > 0) setResult(r);
      setDialog(null);
      load();
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setBusy(false);
    }
  };

  if (loadError && !data) return <Alert severity="error">{loadError}</Alert>;
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const totalAll = data.counts.all;
  const filtering = !!q || !!department || status !== 'all';
  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));
  const first = data.total === 0 ? 0 : (data.page - 1) * data.pageSize + 1;
  const last = Math.min(data.total, data.page * data.pageSize);
  const selectedIds = [...selected];

  if (totalAll === 0 && !filtering) {
    return (
      <Box sx={{ maxWidth: 760 }} data-testid="products-empty">
        <PageTitle title={t('products.title')} />
        <EmptyState
          icon="listAlt"
          title={t('products.empty.title')}
          body={data.locations.length === 0 ? t('products.empty.needLocation') : t('products.empty.body')}
          backgroundColor="#fff"
          border={`1px solid ${BORDER}`}
          borderRadius={10}
          iconColor={ORANGE_INK}
          primaryAction={
            canEdit
              ? data.locations.length === 0
                ? { label: t('products.empty.addLocation'), onClick: () => void navigate('/locations/new') }
                : { label: t('products.add.title'), onClick: () => void navigate('/products/add') }
              : undefined
          }
        />
      </Box>
    );
  }

  // "Needs your answer" is a view of its own: the questions, not a table of rows (§15u).
  const answering = status === 'needs_answer';

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <PageTitle title={t('products.title')} subtitle={t('products.subtitle', { count: totalAll })} />
        </Box>
        {canEdit && (
          <Button
            ariaLabel={t('products.add.title')}
            buttonText={t('products.add.title')}
            variant="contained"
            color="primary"
            startIcon="add"
            onClick={() => void navigate('/products/add')}
            dataTestId="products-add"
          />
        )}
      </Box>

      <Box sx={{ mt: 2.5, display: answering ? 'none' : 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ flex: '1 1 320px' }}>
          <TextField
            ariaLabel={t('products.searchAria')}
            placeholder={t('products.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            dataTestId="products-search"
          />
        </Box>
        {data.locations.length > 0 && (
          <Box sx={{ flex: '0 1 260px', minWidth: 220 }} data-testid="products-location">
            <Select
              ariaLabel={t('products.pricesAt')}
              label={t('products.pricesAt')}
              value={location?.storeId ?? ''}
              onChange={(_e, v) => patchParams({ storeId: v || null })}
              options={data.locations.map((l) => ({ value: l.storeId, label: l.name }))}
              size="small"
              fullWidth
            />
          </Box>
        )}
        <Box sx={{ flex: '0 1 220px', minWidth: 180 }} data-testid="products-department">
          <Select
            ariaLabel={t('products.department')}
            label={t('products.department')}
            value={department || ALL_DEPARTMENTS}
            onChange={(_e, v) => patchParams({ department: v === ALL_DEPARTMENTS ? null : v })}
            options={[
              { value: ALL_DEPARTMENTS, label: t('products.allDepartments') },
              ...data.departments.map((d) => ({ value: d, label: departmentLabel(t, d) })),
            ]}
            size="small"
            fullWidth
          />
        </Box>
      </Box>

      <Box role="group" aria-label={t('products.filterAria')} sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => {
          const active = status === f;
          const count = data.counts[f] ?? 0;
          const attention = ATTENTION.includes(f) && count > 0;
          return (
            <Box key={f} data-testid={`products-filter-${f}`} aria-pressed={active}>
              <Chip
                clickable
                onClick={() => patchParams({ status: f === 'all' ? null : f, qpage: null })}
                label={`${t(`products.filter.${f}`)}  ${count}`}
                aria-label={t('products.filterChipAria', { filter: t(`products.filter.${f}`), count })}
                backgroundColor={active ? '#343F5B' : '#FFFFFF'}
                textColor={active ? '#FFFFFF' : attention ? (f === 'needs_answer' ? ORANGE_INK : RED) : HEADING}
                fontSize={13}
                fontWeight={active || attention ? 700 : 500}
                sx={{ border: `1px solid ${active ? '#343F5B' : attention ? (f === 'needs_answer' ? ORANGE_INK : RED) : '#C9CED6'}` }}
              />
            </Box>
          );
        })}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {loadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {loadError}
        </Alert>
      )}
      {result && <BulkResultNotice result={result} items={items} onClose={() => setResult(null)} />}

      {answering && activeVendorId && (
        <QuestionCards
          vendorId={activeVendorId}
          canResolve={canResolve}
          page={questionPage}
          onPage={(p) => patchParams({ qpage: p === 1 ? null : String(p) }, true)}
          onChanged={load}
        />
      )}

      {!answering && canEdit && selectedIds.length > 0 && location && (
        <Box
          data-testid="products-bulk-bar"
          sx={{ mt: 2, px: 2, py: 1.5, bgcolor: '#343F5B', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}
        >
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 15, mr: 'auto' }}>
            {t('products.bulk.selected', { count: selectedIds.length, location: location.name })}
          </Typography>
          {(
            [
              ['put_on_sale', () => setDialog('put_on_sale')],
              ['change_price', () => setDialog('change_price')],
              ['end_sale', () => void runBulk(selectedIds, { type: 'end_sale' })],
              ['hide', () => void runBulk(selectedIds, { type: 'hide' })],
              ['show', () => void runBulk(selectedIds, { type: 'show' })],
              ['out_of_stock', () => void runBulk(selectedIds, { type: 'out_of_stock' })],
              ['in_stock', () => void runBulk(selectedIds, { type: 'in_stock' })],
            ] as const
          ).map(([key, onClick]) => (
            <Button
              key={key}
              ariaLabel={t(`products.bulk.${key}`)}
              buttonText={t(`products.bulk.${key}`)}
              variant="outlined"
              disabled={busy}
              onClick={onClick}
              dataTestId={`products-bulk-${key}`}
              sx={{ bgcolor: 'transparent', color: '#fff', borderColor: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', borderColor: '#fff' } }}
            />
          ))}
        </Box>
      )}

      {!answering && (
      <>
      <Box
        role="table"
        aria-label={t('products.tableAria')}
        aria-busy={loading}
        sx={{ mt: 2, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', opacity: loading ? 0.6 : 1 }}
      >
        <Box role="row" sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', px: 2.5, py: 1.25, bgcolor: '#F9FAFB', borderBottom: `1px solid ${BORDER}` }}>
          <Box role="columnheader" data-testid="products-select-all">
            {canEdit && (
              <Checkbox
                ariaLabel={t('products.selectAll')}
                checked={allSelected}
                indeterminate={!allSelected && selectedIds.length > 0}
                onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.vendorProductId)))}
                size="small"
              />
            )}
          </Box>
          <HeaderCell>{t('products.columns.product')}</HeaderCell>
          <HeaderCell hideOnPhone>{t('products.columns.identity')}</HeaderCell>
          <HeaderCell hideOnPhone>{location ? t('products.columns.priceAt', { location: location.name }) : t('products.columns.price')}</HeaderCell>
          <HeaderCell hideOnPhone>{t('products.columns.status')}</HeaderCell>
          <HeaderCell hideOnPhone>{t('products.columns.inStock')}</HeaderCell>
          <span />
        </Box>

        {items.length === 0 ? (
          <Typography sx={{ px: 2.5, py: 3, color: MUTED, fontSize: 14 }} data-testid="products-no-match">
            {t('products.noMatch')}
          </Typography>
        ) : (
          items.map((item) => (
            <ProductRow
              key={item.vendorProductId}
              item={item}
              language={i18n.language}
              canEdit={canEdit && !busy && !!location}
              selected={selected.has(item.vendorProductId)}
              onSelect={(on) =>
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (on) next.add(item.vendorProductId);
                  else next.delete(item.vendorProductId);
                  return next;
                })
              }
              onStock={(inStock) => void runBulk([item.vendorProductId], { type: inStock ? 'in_stock' : 'out_of_stock' })}
            />
          ))
        )}
      </Box>

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 14, color: MUTED }} data-testid="products-range">
          {t('products.showing', { first, last, total: data.total })}
        </Typography>
        <Box sx={{ width: 150 }} data-testid="products-page-size">
          <Select
            ariaLabel={t('products.rowsPerPage')}
            label={t('products.rowsPerPage')}
            value={String(pageSize)}
            onChange={(_e, v) => patchParams({ pageSize: v === '25' ? null : v })}
            options={PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))}
            size="small"
            fullWidth
          />
        </Box>
        {totalPages > 1 && (
          <Box sx={{ ml: 'auto' }}>
            <Pagination
              page={data.page}
              totalPages={totalPages}
              onPageChange={(p) => patchParams({ page: p === 1 ? null : String(p) }, true)}
              ariaLabel={t('products.pagination')}
              activeBgColor="#25862D"
              activeColor="#FFFFFF"
              align="right"
            />
          </Box>
        )}
      </Box>
      </>
      )}

      {dialog && location && (
        <BulkDialog
          kind={dialog}
          count={selectedIds.length}
          locationName={location.name}
          busy={busy}
          error={error}
          onCancel={() => {
            setDialog(null);
            setError(null);
          }}
          onSubmit={(action, sale) => void runBulk(selectedIds, action, sale)}
        />
      )}
    </Box>
  );
}

function HeaderCell({ children, hideOnPhone }: { children: string; hideOnPhone?: boolean }) {
  return (
    <Typography
      role="columnheader"
      sx={{ display: hideOnPhone ? { xs: 'none', md: 'block' } : 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: MUTED, textTransform: 'uppercase' }}
    >
      {children}
    </Typography>
  );
}

function ProductRow({
  item,
  language,
  canEdit,
  selected,
  onSelect,
  onStock,
}: {
  item: ProductListItem;
  language: string;
  canEdit: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onStock: (inStock: boolean) => void;
}) {
  const { t } = useTranslation();
  const soldHere = item.priceCents != null;
  const onSale = item.salePriceCents != null;
  const meta = [item.brand, item.size].filter(Boolean).join(' · ');

  return (
    <Box
      role="row"
      data-testid={`products-row-${item.vendorProductId}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 2,
        alignItems: 'center',
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${BORDER}`,
        bgcolor: selected ? '#EDF6EE' : '#fff',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box role="cell" data-testid={`products-row-check-${item.vendorProductId}`}>
        {canEdit && (
          <Checkbox
            ariaLabel={t('products.selectOne', { name: item.name })}
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            size="small"
          />
        )}
      </Box>

      <Box
        role="cell"
        component={Link}
        to={`/products/${item.vendorProductId}`}
        data-testid={`products-row-link-${item.vendorProductId}`}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, textDecoration: 'none', color: 'inherit' }}
      >
        <ProductThumb src={item.imageUrl} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: HEADING, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.name}
          </Typography>
          {meta && <Typography sx={{ fontSize: 13, color: MUTED }}>{meta}</Typography>}
        </Box>
      </Box>

      <Box role="cell" sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: '#343434' }}>{t('products.glpc', { glpc: item.glpProductId })}</Typography>
        {item.barcode.kind === 'assigned' && (
          <Typography sx={{ fontSize: 12, color: ORANGE_INK, fontWeight: 700 }}>{t('products.barcode.assigned', { barcode: item.barcode.value })}</Typography>
        )}
        {item.barcode.kind === 'own' && <Typography sx={{ fontSize: 12, color: '#343434' }}>{t('products.barcode.own', { barcode: item.barcode.value })}</Typography>}
        {item.barcode.kind === 'none' && <Typography sx={{ fontSize: 12, color: RED, fontWeight: 700 }}>{t('products.barcode.none')}</Typography>}
        {item.vendorSku && <Typography sx={{ fontSize: 12, color: MUTED }}>{t('products.sku', { sku: item.vendorSku })}</Typography>}
      </Box>

      <Box role="cell" sx={{ display: { xs: 'none', md: 'block' } }}>
        {!soldHere ? (
          <Typography sx={{ fontSize: 14, color: MUTED }}>{t('products.notSoldHere')}</Typography>
        ) : onSale ? (
          <>
            <Typography sx={{ fontSize: 12, color: MUTED }}>{t('products.basePrice', { price: formatCents(item.priceCents) })}</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: ORANGE_INK }}>
              {item.saleEndsOn
                ? t('products.saleUntil', { price: formatCents(item.salePriceCents), date: formatDay(item.saleEndsOn, language) })
                : t('products.saleNoEnd', { price: formatCents(item.salePriceCents) })}
            </Typography>
            {item.saleStartsOn && item.saleStartsOn > todayDay() && (
              <Typography sx={{ fontSize: 12, color: MUTED }} data-testid={`products-row-sale-starts-${item.vendorProductId}`}>
                {t('products.saleStarts', { date: formatDay(item.saleStartsOn, language) })}
              </Typography>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{formatCents(item.priceCents)}</Typography>
        )}
      </Box>

      <Box role="cell" sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.75, flexWrap: 'wrap' }}>
        <ProductStatusChip status={item.status} />
        {item.recalled && <FlagChip kind="recalled" />}
        {onSale && <FlagChip kind="on_sale" />}
      </Box>

      <Box role="cell" sx={{ display: { xs: 'none', md: 'block' } }} data-testid={`products-row-stock-${item.vendorProductId}`}>
        {soldHere && (
          <Switch
            ariaLabel={t('products.inStockAria', { name: item.name })}
            checked={item.inStock}
            disabled={!canEdit}
            onChange={(_e, checked) => onStock(checked)}
            size="small"
          />
        )}
      </Box>

      <Box component={Link} to={`/products/${item.vendorProductId}`} aria-label={t('products.openAria', { name: item.name })} sx={{ display: 'flex' }}>
        <Icon name="chevronRight" size={20} color={GREEN_INK} />
      </Box>
    </Box>
  );
}

function BulkResultNotice({ result, items, onClose }: { result: BulkResult; items: ProductListItem[]; onClose: () => void }) {
  const { t } = useTranslation();
  const nameOf = (id: string) => items.find((i) => i.vendorProductId === id)?.name ?? id;
  return (
    <Alert severity={result.skipped.length ? 'warning' : 'success'} sx={{ mt: 2 }} onClose={onClose} data-testid="products-bulk-result">
      <Typography sx={{ fontWeight: 700 }}>{t('products.bulk.done', { count: result.updated })}</Typography>
      {result.unchanged > 0 && <Typography sx={{ fontSize: 14 }}>{t('products.bulk.unchanged', { count: result.unchanged })}</Typography>}
      {result.skipped.map((s) => (
        <Typography key={s.vendorProductId} sx={{ fontSize: 14 }}>
          {t(`products.bulk.skipped.${s.reason}`, { name: nameOf(s.vendorProductId) })}
        </Typography>
      ))}
    </Alert>
  );
}
